import { getTime } from "@suse-o11y/date-time";
import type { Time } from "@suse-o11y/date-time/time";
import { Logger } from "@suse-o11y/logger";
import { toKeys } from "@suse-o11y/to-keys";
import { toValues } from "@suse-o11y/to-values";
import { isRight } from "fp-ts/es6/Either";
import cloneDeep from "lodash/cloneDeep";
import flowRight from "lodash/flowRight";
import groupBy from "lodash/groupBy";
import isArray from "lodash/isArray";
import isObject from "lodash/isObject";
import uniq from "lodash/uniq";
import type { ValidationError } from "yup";

import { type NodeId, type QueryMetadata, type QueryView, Type, type Version } from "~/models/dto";

import { TraversalData, parseQuery } from "../../../../components/react/topology-query/TopologyQueryFunctions";
import { validateSync } from "../../../../generic/Util";

import { DEFAULT_VIEW_REPLACEMENT_QUERY, viewPointSchema } from "./ViewPointQueryUtils";

export const logger = Logger.namespace("ViewPointStateLogger");

export const metaDataFromView = (view: QueryView, queryTime: undefined | Time): QueryMetadata => ({
	_type: Type.QueryMetadata,
	groupingEnabled: view.groupingEnabled,
	showIndirectRelations: view.showIndirectRelations,
	minGroupSize: view.minimumGroupSize,
	groupedByLayer: view.groupedByLayers,
	groupedByDomain: view.groupedByDomains,
	groupedByRelation: view.groupedByRelations,
	autoGrouping: view.autoGrouping,
	connectedComponents: view.connectedComponents,
	neighboringComponents: view.neighboringComponents,
	showFullComponent: true,
	queryTime: getTime(queryTime),
});

export const metaDataComponentsOnly = (queryTime: undefined | Time): QueryMetadata => ({
	_type: Type.QueryMetadata,
	showIndirectRelations: false,
	minGroupSize: 0,
	autoGrouping: false,
	groupingEnabled: false,
	groupedByLayer: false,
	groupedByDomain: false,
	groupedByRelation: false,
	connectedComponents: false,
	neighboringComponents: false,
	showFullComponent: false,
	queryTime: getTime(queryTime),
});

const LABEL_TYPE = Type.Label.toLowerCase();

export const mergeDetachedFiltersIntoViewQuery = ({
	query,
	detachedFilters,
	supportedFilters,
}: {
	query: string;
	detachedFilters: Array<string>;
	supportedFilters: Array<string>;
}): string => {
	const viewSupportedDetachedFilters = uniq(
		detachedFilters.filter((labelFilter) => supportedFilters.some((forView) => labelFilter.startsWith(`${forView}:`))),
	);
	const trimmedQuery = query.trim();

	if (viewSupportedDetachedFilters.length === 0 || (trimmedQuery.length > 0 && parseQuery(trimmedQuery).isLeft())) {
		return trimmedQuery;
	}

	const filterClause = TraversalData.empty
		.replaceFiltersFor(LABEL_TYPE, groupLabelsByPrefixes(viewSupportedDetachedFilters))
		.toSTQL();

	if (filterClause.trim().length === 0) {
		return trimmedQuery;
	}

	return trimmedQuery.length === 0 ? filterClause : `(${trimmedQuery}) AND (${filterClause})`;
};

const labelNameRegex = /^([^:]+):(.*)$/u;

const groupLabelsByPrefixes = (labels: Array<string>): Array<Array<string>> => {
	const labelsGroupedByPrefix = groupBy(labels, (label) => {
		const match = label.match(labelNameRegex);
		return match ? match[1] : "";
	});

	// 2d array of labels grouped by prefixes
	return toValues(labelsGroupedByPrefix);
};

export const forValidatedQuery = <R>(query: string, forValidQuery: (query: string) => R): R | undefined =>
	parseQuery(query || DEFAULT_VIEW_REPLACEMENT_QUERY).fold(() => undefined, forValidQuery);

export const clearIdAndLastUpdateTimestampOf = <T extends { id?: NodeId; lastUpdateTimestamp?: number }>(
	newView: T,
): T => {
	function clearProperty(property: any) {
		if (!property) {
			return;
		}
		if (isObject(property)) {
			clearIdAndLastUpdateTimestampOf(property);
		}
		if (isArray(property)) {
			property.forEach(clearProperty);
		}
	}

	newView.id = undefined;
	newView.lastUpdateTimestamp = undefined;

	toKeys(newView).forEach((key) => {
		clearProperty(newView[key]);
	});
	return newView;
};

const clearOwnedByAndIdentifier = (newView: QueryView): QueryView => ({
	...newView,
	ownedBy: undefined,
	identifier: undefined,
});

const clearFlags = (newView: QueryView): QueryView => ({
	...newView,
	flags: undefined,
});

const clearUndesiredFields = flowRight(clearIdAndLastUpdateTimestampOf, clearOwnedByAndIdentifier, clearFlags);

export const createSaveAsView = (view: QueryView, version: Version): QueryView => {
	return clearUndesiredFields({
		...cloneDeep(view),
		queryVersion: version,
		name: "", // User needs to provide a new view name
	});
};

export const validateView = (view: QueryView): ValidationError | undefined => {
	const error = validateSync(viewPointSchema, view);
	if (error != null) {
		logger.error(error);
	}
	return error;
};

export const isViewQueryValid = (query: string): boolean => isRight(parseQuery(query));
